<template>
  <PageHeader
    breadcrumb
    :items="[
      { label: $t('ui.entities.setting', 2), to: '/settings' },
      { label: $t('ui.common.user', 2), to: '/settings/users' }
    ]"
  >
    <template #right>
      <Button
        v-if="$can('create_users')"
        :label="$t('ui.crud.new', { item: $t('ui.common.user') })"
        severity="primary"
        icon="plus"
        @click="newUserState.isOpen = true"
      />
    </template>
  </PageHeader>

  <Card>
    <UsersTable
      v-model="usersList"
      :is-loading="usersApiRequest.isLoading.value"
      @user-updated="userDataUpdated"
    />
  </Card>

  <teleport to="#modals-container">
    <Modal
      :is-open="newUserState.isOpen"
      :buttons="[]"
      :has-unsaved-changes="newUserState.hasUnsavedChanges"
      :is-confirming="newUserState.isSubmitting"
      :disable-close="newUserState.isSubmitting"
      min-width="550"
      @close-modal="handleCloseNewUserModal"
    >
      <NewUser
        :roles-list="rolesList"
        @form-updated="newUserState.hasUnsavedChanges = true"
        @is-submitting="onNewUserIsSubmitting"
        @completed="handleNewUserSubmitCompleted"
      />
    </Modal>
  </teleport>
</template>

<script setup lang="ts">
// Vue
import { ref, reactive, computed, onMounted } from 'vue'
// Components
import PageHeader from '@/components/page/PageHeader.vue'
import Card from '@/components/card/Card.vue'
import Button from '@/components/button/Button.vue'
import Modal from '@/components/modal/Modal.vue'
// Unique components
import UsersTable from '@/components/unique/tables/UsersTable.vue'
import NewUser from '@/components/unique/forms/NewUser.vue'
// Composables
import useApiRequest from '@/composables/useApiRequest'
// Stores
import { useAuthStore } from '@/stores/AuthStore'
// Types
import type { User, Role, UserAccessFormatted, UserAccessRaw, ApiUser } from '@/types/domain/user'

const authStore = useAuthStore()
/**
 * Users table data
 */
const usersList = ref<User[]>([])

/** Modal state for creating new users */
const newUserState = reactive({
  isOpen: false,
  hasUnsavedChanges: false,
  isSubmitting: false
})

/** API request composables */
const usersApiRequest = useApiRequest()
const caslApiRequest = useApiRequest()

/**
 * Query params accepted by the Users API
 */
type ApiQueryParams = {
  userType: 'all' | 'externalUsers' | string
  showDisabled: boolean
  level?: number | null
  dealerships?: string
}

/**
 * Initialize and hold the API query parameters based on the session user's access
 */
const apiQueryParams = ref<ApiQueryParams>(initializeApiQueryParams())

onMounted(async () => {
  await caslApiRequest.send({ endpoint: '/v1/roles' })
  await fetchUsers()
})

/**
 * Fetch the users list and map server response into enriched `User` objects
 */
async function fetchUsers() {
  await usersApiRequest.send({
    endpoint: '/v1/users',
    params: apiQueryParams.value
  })

  const allRoles = ((caslApiRequest.data.value as any)?.roles || []) as Role[]
  const rolesById = new Map<number, Role>(allRoles.map(r => [r.id, r]))

  const apiUsers = (usersApiRequest.data.value ?? []) as unknown as ApiUser[]
  usersList.value = apiUsers.map(u => formatUser(u, rolesById))
}

/**
 * Roles list limited to current user's level, for the New User form
 */
const rolesList = computed<Role[] | undefined>(() => {
  const rolesArray = ((caslApiRequest.data.value as any)?.roles || []) as Role[] | undefined
  const sessionUserLevel = authStore.details.level as number | null
  if (sessionUserLevel == null) return rolesArray
  return rolesArray?.filter((role: Role) => role.level <= sessionUserLevel)
})

/** Close and reset the New User modal after successful submit */
const handleNewUserSubmitCompleted = () => {
  newUserState.isOpen = false
  newUserState.hasUnsavedChanges = false
  newUserState.isSubmitting = false
  fetchUsers()
}

/** Merge updated user data into the in-memory list */
function userDataUpdated(userId: number, newData: User) {
  const user = ((usersApiRequest.data.value as User[] | null) ?? []).find((u: User) => u.id === userId)
  if (user) Object.assign(user, newData)
}

/** Toggle submitting state for New User flow */
const onNewUserIsSubmitting = (value: boolean) => {
  newUserState.isSubmitting = value
}

/** Close handler for the New User modal */
const handleCloseNewUserModal = () => {
  newUserState.isOpen = false
  newUserState.hasUnsavedChanges = false
}

/**
 * Build initial API query params based on current user's access
 */
function initializeApiQueryParams(): ApiQueryParams {
  const base: ApiQueryParams = { userType: 'all', showDisabled: true }
  if (authStore.details.userType === 2) {
    const dealershipIDs = (authStore.accessAllowed.dealerships as Array<{ id: number }>).map(d => d.id)
    return {
      userType: 'externalUsers',
      showDisabled: false,
      level: authStore.details.level as number | null,
      dealerships: dealershipIDs.join(',')
    }
  }
  return base
}

/**
 * Convert raw access from API into formatted structure with All flags
 */
function formatAccess(raw: UserAccessRaw): UserAccessFormatted {
  const mapField = (value: '*' | number[]) => ({
    list: value === '*' ? [] : (value as number[]),
    all: value === '*'
  })
  const regions = mapField(raw.regions)
  const manufacturers = mapField(raw.manufacturers)
  const groups = mapField(raw.groups)
  const dealerships = mapField(raw.dealerships)
  return {
    regions: regions.list,
    regionsAll: regions.all,
    manufacturers: manufacturers.list,
    manufacturersAll: manufacturers.all,
    groups: groups.list,
    groupsAll: groups.all,
    dealerships: dealerships.list,
    dealershipsAll: dealerships.all
  }
}

/**
 * Return label for a numeric user type
 */
function userTypeName(userType: number): string {
  switch (userType) {
    case 1:
      return 'Internal'
    case 2:
      return 'External'
    default:
      return 'Unknown'
  }
}

/**
 * Enrich API user with named roles, userType label and formatted access
 */
function formatUser(apiUser: ApiUser, rolesById: Map<number, Role>): User {
  const rolesNamed = apiUser.roles.map(id => rolesById.get(id)).filter(Boolean) as Role[]
  return {
    ...apiUser,
    rolesNamed,
    userTypeNamed: userTypeName(apiUser.userType),
    access: formatAccess(apiUser.access as UserAccessRaw)
  }
}
</script>
